import { api } from '@/shared/api'
import { forgetAllGames, myGames } from '@/shared/game/storage'
import { landingSource } from './landing'
import type { Profile, UserCard } from './types'

/**
 * Вход и регистрация.
 *
 * Токена нет: сессия живёт в куке, которую ставит сервер, поэтому «войти»
 * здесь означает ровно один запрос — ничего сохранять в localStorage не надо
 * и нечего терять при чистке браузера.
 */

export async function fetchMe(): Promise<UserCard | null> {
  // Ответ всегда 200: «никто не вошёл» — это user: null, а не ошибка.
  const { data } = await api.get<{ user: UserCard | null }>('/auth/me')

  return data.user
}

export async function register(input: {
  nickname: string
  email: string
  password: string
  /** Повтор пароля: правило confirmed на сервере ждёт именно это поле. */
  password_confirmation: string
}): Promise<UserCard> {
  /*
   * Секреты гостевых мест из этого браузера: по ним сервер находит, с кем
   * человек играл до регистрации, и награждает пригласившего
   * (docs/QUESTS.md, «Приглашение»). Не больше двадцати свежих — столько
   * сервер и разбирает.
   */
  const seats = myGames()
    .slice(0, 20)
    .map((game) => game.secret)

  const utm = landingSource()

  const { data } = await api.post<UserCard>('/auth/register', {
    ...input,
    seats,
    ...(utm === null ? {} : { utm_source: utm }),
  })

  return data
}

export async function login(input: { login: string; password: string }): Promise<UserCard> {
  const { data } = await api.post<UserCard>('/auth/login', input)

  return data
}

export async function logout(): Promise<void> {
  await api.post('/auth/logout')

  // Вместе с сессией забываем и секреты партий: они такое же
  // удостоверение, и с ними вышедший продолжал бы ходить своими фишками.
  forgetAllGames()
}

/**
 * Поставить аватарку.
 *
 * Файлом, а не адресом: картинка ложится в хранилище приложения, и адрес
 * ей выдаёт сервер — со своей версией, чтобы браузер не держал старую.
 */
export async function uploadAvatar(file: File): Promise<UserCard> {
  const form = new FormData()
  form.append('avatar', file)

  const { data } = await api.post<UserCard>('/me/avatar', form)

  return data
}

/** Убрать аватарку: вместо неё снова буква ника. */
export async function removeAvatar(): Promise<UserCard> {
  const { data } = await api.delete<UserCard>('/me/avatar')

  return data
}

/**
 * Забыли пароль: попросить письмо со ссылкой.
 *
 * Ответ одинаковый, есть такая почта или нет: иначе форма превращается
 * в проверку, зарегистрирован ли человек.
 */
export async function forgotPassword(email: string): Promise<string> {
  const { data } = await api.post<{ message: string }>('/auth/forgot', { email })

  return data.message
}

/** Задать новый пароль по ссылке из письма. */
export async function resetPassword(input: {
  token: string
  email: string
  password: string
  password_confirmation: string
}): Promise<string> {
  const { data } = await api.post<{ message: string }>('/auth/reset', input)

  return data.message
}

/** Изменить пароль, зная текущий. */
export async function changePassword(input: {
  current: string
  password: string
  password_confirmation: string
}): Promise<string> {
  const { data } = await api.patch<{ message: string }>('/me/password', input)

  return data.message
}

/** Изменить почту — тоже под текущий пароль. */
export async function changeEmail(input: { current: string; email: string }): Promise<string> {
  const { data } = await api.patch<{ message: string }>('/me/email', input)

  return data.message
}

/** Свой профиль со статистикой. */
export async function fetchProfile(): Promise<Profile> {
  const { data } = await api.get<Profile>('/me')

  return data
}

/** Чужой профиль: статистика открыта, хвастаться ею и есть смысл. */
export async function fetchUser(userId: number): Promise<Profile> {
  const { data } = await api.get<Profile>(`/users/${userId}`)

  return data
}
